/**
 * Scraper firms → jobs.company (exact DB value) + UI display name.
 * Slugs are kebab-case article / rail ids (not necessarily scraper folder names).
 *
 * Keep `jobsCompany` equal to whatever scrapers write into `jobs.company`.
 * Never rename DB values here for cosmetics — only `displayName` is for UI.
 */
export const JOBS_COMPANY_BY_SLUG = {
  apollo: { jobsCompany: "Apollo", displayName: "Apollo" },
  "aqr-capital-management": { jobsCompany: "AQR Capital Management", displayName: "AQR Capital Management" },
  "bank-of-america": { jobsCompany: "Bank of America", displayName: "Bank of America" },
  barclays: { jobsCompany: "Barclays", displayName: "Barclays" },
  blackstone: { jobsCompany: "Blackstone", displayName: "Blackstone" },
  "bnp-paribas": { jobsCompany: "BNP Paribas", displayName: "BNP Paribas" },
  citadel: { jobsCompany: "Citadel", displayName: "Citadel" },
  citigroup: { jobsCompany: "Citigroup", displayName: "Citigroup" },
  evercore: { jobsCompany: "Evercore", displayName: "Evercore" },
  "goldman-sachs": { jobsCompany: "Goldman Sachs", displayName: "Goldman Sachs" },
  guggenheim: { jobsCompany: "Guggenheim", displayName: "Guggenheim" },
  hsbc: { jobsCompany: "HSBC", displayName: "HSBC" },
  "hudson-river-trading": { jobsCompany: "Hudson River Trading", displayName: "Hudson River Trading" },
  "jane-street": { jobsCompany: "Jane Street", displayName: "Jane Street" },
  jefferies: { jobsCompany: "Jefferies", displayName: "Jefferies" },
  jpmorgan: { jobsCompany: "jpmorgan", displayName: "J.P. Morgan" },
  kkr: { jobsCompany: "KKR", displayName: "KKR" },
  "millennium-management": { jobsCompany: "Millennium Management", displayName: "Millennium Management" },
  mizuho: { jobsCompany: "Mizuho", displayName: "Mizuho" },
  "morgan-stanley": { jobsCompany: "Morgan Stanley", displayName: "Morgan Stanley" },
  "pjt-partners": { jobsCompany: "PJT Partners", displayName: "PJT Partners" },
  point72: { jobsCompany: "Point72", displayName: "Point72" },
  revolut: { jobsCompany: "Revolut", displayName: "Revolut" },
  rothschild: { jobsCompany: "Rothschild & Co", displayName: "Rothschild & Co" },
  stripe: { jobsCompany: "Stripe", displayName: "Stripe" },
  "two-sigma": { jobsCompany: "Two Sigma", displayName: "Two Sigma" },
};

const DISPLAY_BY_JOBS_COMPANY = (() => {
  /** @type {Record<string, string>} */
  const map = {};
  for (const entry of Object.values(JOBS_COMPANY_BY_SLUG)) {
    if (entry?.jobsCompany && entry?.displayName) {
      map[String(entry.jobsCompany).toLowerCase()] = entry.displayName;
    }
  }
  return map;
})();

export function normalizeJobsCompanySlug(raw) {
  return String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");
}

export function getJobsCompanyEntry(slug) {
  const key = normalizeJobsCompanySlug(slug);
  return JOBS_COMPANY_BY_SLUG[key] || null;
}

/** Exact `jobs.company` value for API filters / scraper parity. */
export function resolveJobsCompanyName({ companySlug = "", firmName = "" } = {}) {
  const entry = getJobsCompanyEntry(companySlug);
  if (entry?.jobsCompany) return entry.jobsCompany;
  const firm = String(firmName || "").trim();
  if (firm) return firm;
  return "";
}

/**
 * Pretty label for UI from a DB `jobs.company` value (or slug-like string).
 * Does not change the underlying scraper/DB identity.
 */
export function formatJobsCompanyLabel(company) {
  const raw = String(company || "").trim();
  if (!raw) return "";
  const byDb = DISPLAY_BY_JOBS_COMPANY[raw.toLowerCase()];
  if (byDb) return byDb;
  const entry = getJobsCompanyEntry(raw);
  if (entry?.displayName) return entry.displayName;
  return raw;
}

/** Logo filename stem under /images/logos/{stem}.avif — tied to DB company, not display label. */
export function jobsCompanyLogoStem(company) {
  return String(company || "")
    .trim()
    .toLowerCase()
    .replaceAll(" ", "-");
}

export function resolveJobsDisplayName({ companySlug = "", firmName = "" } = {}) {
  const entry = getJobsCompanyEntry(companySlug);
  if (entry?.displayName) return entry.displayName;
  const firm = String(firmName || "").trim();
  if (firm) return formatJobsCompanyLabel(firm);
  const slug = normalizeJobsCompanySlug(companySlug);
  if (!slug) return "";
  return slug
    .split("-")
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

export function listJobsCompanySlugs() {
  return Object.keys(JOBS_COMPANY_BY_SLUG);
}
